<template>
  <span
    :class="[
      'tree-rollback-bar',
      'inline-flex items-center w-200% min-w-160px select-none touch-none relative left--50%',
      {
        'tree-rollback-bar--active': active,
        'tree-rollback-bar--disabled': disabled,
        'cursor-grab active:cursor-grabbing': !disabled,
        'cursor-not-allowed opacity-78': disabled,
      },
    ]"
    title="退回控制棒"
    role="separator"
    :tabindex="disabled ? -1 : 0"
    :aria-disabled="disabled ? 'true' : 'false'"
    @pointerdown.prevent.stop="handlePointerDown"
  >
    <span class="tree-rollback-bar-line inline-block flex-1 h-3px" />
  </span>
</template>

<script setup lang="ts">
/**
 * FeatureManager 退回控制棒标签。
 *
 * 文件定位：
 * 1. 当前组件只负责退回控制棒在设计树中的视觉表达；
 * 2. 拖拽、键盘和右键动作仍由 FeatureTree 统一绑定；
 * 3. 单独拆出可避免 FeatureTree 继续承载退回控制棒的视觉细节。
 */
const props = defineProps<{
  /** 是否存在处于控制棒下方的退回特征。 */
  active: boolean;
  /** 草图绘制过程中退回控制棒不可操作。 */
  disabled?: boolean;
}>();

const emit = defineEmits<{
  /** 通知 FeatureTree 进入退回控制棒拖拽流程。 */
  (event: 'rollback-drag-start', value: PointerEvent): void;
}>();

/**
 * 发出退回控制棒拖拽开始事件。
 *
 * @param event 指针按下事件。
 */
function handlePointerDown(event: PointerEvent): void {
  if (props.disabled) {
    return;
  }

  /**
   * 组件本身不直接移动 cursor，只把明确的指针入口交给 FeatureTree。
   * 这样可以避免视觉组件直接依赖 store，同时绕开 Naive Tree 对原生 drag 事件的包裹差异。
   */
  emit('rollback-drag-start', event);
}
</script>

<style scoped>
.tree-rollback-bar {
  --tree-rollback-bar-color: var(--n-primary-color, var(--puppy-primary));
  --tree-rollback-bar-shadow-color: var(--n-primary-color-pressed, rgba(32, 128, 240, 0.32));
  /**
   * 视觉线只有 3px，过难点中。
   * padding 扩大可抓取/可变成小手的上下热区；负 margin 抵消对相邻树行的额外撑开。
   */
  padding-block: 10px;
  margin-block: -8px;
}

.tree-rollback-bar-line {
  background: var(--tree-rollback-bar-color);
}

.tree-rollback-bar--active {
  --tree-rollback-bar-color: var(--n-primary-color-hover, #4098fc);
}

.tree-rollback-bar--disabled {
  --tree-rollback-bar-color: var(--n-text-color-disabled, #9ca3af);
  --tree-rollback-bar-shadow-color: var(--n-border-color, #d9d9d9);
}
</style>
